'use client';

import { useMemo, useState } from 'react';
import { Dna, Shuffle, Trophy } from 'lucide-react';
import {
  GREENPRO_OPERATIONAL_PROFILE_OPTIONS,
  type GreenProOperationalProfile,
} from '@/lib/operationalAccounts';
import ModalPortal from '@/components/ui/ModalPortal';
import { useLanguage } from '@/context/LanguageContext';

interface OperationalProfileSwitcherProps {
  value: GreenProOperationalProfile;
  onChange: (value: GreenProOperationalProfile) => void | Promise<void>;
  disabled?: boolean;
  compact?: boolean;
  variant?: 'cards' | 'inline';
  allowCurrentSelection?: boolean;
}

export default function OperationalProfileSwitcher({
  value,
  onChange,
  disabled = false,
  compact = false,
  variant = 'cards',
  allowCurrentSelection = false,
}: OperationalProfileSwitcherProps) {
  const { t } = useLanguage();
  const [pendingProfile, setPendingProfile] = useState<GreenProOperationalProfile | null>(null);
  const [confirmationContext, setConfirmationContext] = useState<{
    from: GreenProOperationalProfile;
    to: GreenProOperationalProfile;
  } | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const profileMeta = useMemo(
    () => ({
      surebet: {
        icon: Shuffle,
        accent: 'text-violet-300',
        selectedClass: 'border-violet-400/50 bg-violet-500/10 shadow-[0_0_0_1px_rgba(196,181,253,0.18)]',
        idleClass: 'border-white/10 bg-white/[0.03] hover:border-violet-400/35 hover:bg-violet-500/[0.08]',
        badgeClass: 'border-violet-400/25 bg-violet-500/12 text-violet-200',
        hoverTitleKey: 'operationalProfile.surebet.hoverTitle',
        hoverDescriptionKey: 'operationalProfile.surebet.hoverDescription',
      },
      trader: {
        icon: Trophy,
        accent: 'text-sky-300',
        selectedClass: 'border-sky-400/50 bg-sky-500/10 shadow-[0_0_0_1px_rgba(56,189,248,0.16)]',
        idleClass: 'border-white/10 bg-white/[0.03] hover:border-sky-400/35 hover:bg-sky-500/[0.08]',
        badgeClass: 'border-sky-400/25 bg-sky-500/12 text-sky-200',
        hoverTitleKey: 'operationalProfile.trader.hoverTitle',
        hoverDescriptionKey: 'operationalProfile.trader.hoverDescription',
      },
      hybrid: {
        icon: Dna,
        accent: 'text-emerald-300',
        selectedClass: 'border-emerald-400/50 bg-emerald-500/10 shadow-[0_0_0_1px_rgba(16,185,129,0.22)]',
        idleClass: 'border-white/10 bg-white/[0.03] hover:border-emerald-400/35 hover:bg-emerald-500/[0.08]',
        badgeClass: 'border-emerald-400/25 bg-emerald-500/12 text-emerald-200',
        hoverTitleKey: 'operationalProfile.hybrid.hoverTitle',
        hoverDescriptionKey: 'operationalProfile.hybrid.hoverDescription',
      },
    }),
    []
  );

  const currentOption = GREENPRO_OPERATIONAL_PROFILE_OPTIONS.find((option) => option.value === value);
  const pendingOption = pendingProfile
    ? GREENPRO_OPERATIONAL_PROFILE_OPTIONS.find((option) => option.value === pendingProfile) || null
    : null;
  const confirmationFromProfile = confirmationContext?.from ?? value;
  const confirmationToProfile = confirmationContext?.to ?? pendingOption?.value ?? null;

  function getOptionLabel(profile: GreenProOperationalProfile) {
    const option = GREENPRO_OPERATIONAL_PROFILE_OPTIONS.find((entry) => entry.value === profile);
    return t(`operationalProfile.${profile}.label`) || option?.label || profile;
  }

  function getOptionDescription(profile: GreenProOperationalProfile) {
    const option = GREENPRO_OPERATIONAL_PROFILE_OPTIONS.find((entry) => entry.value === profile);
    return t(`operationalProfile.${profile}.description`) || option?.description || '';
  }

  async function handleProfileSelection(nextValue: GreenProOperationalProfile) {
    if (disabled) return;
    if (nextValue === value && !allowCurrentSelection) return;
    setPendingProfile(nextValue);
    setConfirmationContext({ from: value, to: nextValue });
  }

  async function confirmProfileChange() {
    const targetProfile = confirmationContext?.to ?? pendingProfile;
    if (!targetProfile) return;
    setIsConfirming(true);
    try {
      await onChange(targetProfile);
      setPendingProfile(null);
      setConfirmationContext(null);
    } finally {
      setIsConfirming(false);
    }
  }

  function resetPendingConfirmation() {
    setPendingProfile(null);
    setConfirmationContext(null);
  }

  if (variant === 'inline') {
    return (
      <>
        <div className="flex flex-wrap items-center gap-2">
          {GREENPRO_OPERATIONAL_PROFILE_OPTIONS.map((option) => {
            const selected = option.value === value;
            const meta = profileMeta[option.value];
            const Icon = meta.icon;
            const optionLabel = getOptionLabel(option.value);
            const optionDescription = t(meta.hoverDescriptionKey);

            return (
              <button
                key={option.value}
                type="button"
                disabled={disabled}
                onClick={() => void handleProfileSelection(option.value)}
                title={optionDescription}
                className={[
                  'group inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-all',
                  selected ? `${meta.selectedClass} ${meta.accent}` : `${meta.idleClass} text-zinc-300`,
                  disabled ? 'cursor-not-allowed opacity-60' : '',
                ].join(' ')}
              >
                <Icon size={12} />
                {optionLabel}
              </button>
            );
          })}
        </div>
        {pendingOption ? (
          <ModalPortal>
            <div data-operational-profile-modal="true" className="modal-shell-overlay px-4">
              <div className="modal-shell-panel max-w-lg overflow-hidden">
                <div className="border-b border-white/10 px-5 py-4 sm:px-6">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-300/80">{t('operationalProfile.confirmTitle')}</p>
                    <p className="mt-2 text-lg font-black text-white">{t('operationalProfile.confirmMessage').replace('{current}', getOptionLabel(confirmationFromProfile)).replace('{next}', confirmationToProfile ? getOptionLabel(confirmationToProfile) : getOptionLabel(pendingOption.value))}</p>
                </div>
                <div className="space-y-4 px-5 py-5 sm:px-6">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                      <p className="text-sm font-semibold text-white">{t(profileMeta[pendingOption.value].hoverTitleKey)}</p>
                      <p className="mt-2 text-sm leading-6 text-zinc-300">{t(profileMeta[pendingOption.value].hoverDescriptionKey)}</p>
                      <p className="mt-3 text-xs text-zinc-400">{t('operationalProfile.changeHint')}</p>
                  </div>
                    <div className="flex flex-col-reverse justify-end gap-2 sm:flex-row">
                      <button type="button" onClick={resetPendingConfirmation} className="rounded-lg border border-white/10 px-4 py-2 text-xs font-semibold text-zinc-300 transition-colors hover:border-white/20 hover:text-white">
                        {t('common.cancel')}
                    </button>
                    <button type="button" disabled={disabled || isConfirming} onClick={() => void confirmProfileChange()} className="rounded-lg bg-brand-700 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-60">
                        {isConfirming ? t('operationalProfile.confirmLoading') : t('operationalProfile.confirmAction')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </ModalPortal>
        ) : null}
      </>
    );
  }

  return (
    <>
      <div className={compact ? 'grid grid-cols-1 gap-2' : 'grid grid-cols-1 gap-3 md:grid-cols-3'}>
        {GREENPRO_OPERATIONAL_PROFILE_OPTIONS.map((option) => {
          const selected = option.value === value;
          const meta = profileMeta[option.value];
          const Icon = meta.icon;
          const optionLabel = getOptionLabel(option.value);
          const optionDescription = getOptionDescription(option.value);

          return (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              onClick={() => void handleProfileSelection(option.value)}
              className={[
                'group relative overflow-hidden rounded-2xl border px-4 py-3 text-left transition-all',
                compact ? 'min-h-[84px]' : 'min-h-[118px]',
                selected ? meta.selectedClass : meta.idleClass,
                disabled ? 'cursor-not-allowed opacity-60' : '',
              ].join(' ')}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${meta.badgeClass}`}>
                    <Icon size={12} /> {optionLabel}
                  </span>
                  <p className={`mt-3 text-sm font-semibold ${selected ? meta.accent : 'text-white'}`}>{optionLabel}</p>
                  <p className="mt-1 text-xs leading-5 text-zinc-400">{optionDescription}</p>
                </div>
                <Icon size={18} className={selected ? meta.accent : 'text-zinc-500 transition-colors group-hover:text-white'} />
              </div>
              <div className="mt-3 rounded-xl border border-white/8 bg-black/20 px-3 py-2 opacity-100 transition-all group-hover:border-white/15 group-hover:bg-black/30">
                <p className={`text-[11px] font-semibold ${meta.accent}`}>{t(meta.hoverTitleKey)}</p>
                <p className="mt-1 text-[11px] leading-5 text-zinc-400">{t(meta.hoverDescriptionKey)}</p>
              </div>
            </button>
          );
        })}
      </div>
      {pendingOption ? (
        <ModalPortal>
          <div data-operational-profile-modal="true" className="modal-shell-overlay px-4">
            <div className="modal-shell-panel max-w-lg overflow-hidden">
              <div className="border-b border-white/10 px-5 py-4 sm:px-6">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-300/80">{t('operationalProfile.confirmTitle')}</p>
                <p className="mt-2 text-lg font-black text-white">{t('operationalProfile.confirmMessage').replace('{current}', currentOption ? getOptionLabel(confirmationFromProfile) : t('operationalProfile.currentFallback')).replace('{next}', confirmationToProfile ? getOptionLabel(confirmationToProfile) : getOptionLabel(pendingOption.value))}</p>
              </div>
              <div className="space-y-4 px-5 py-5 sm:px-6">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <p className={`text-sm font-semibold ${profileMeta[pendingOption.value].accent}`}>{t(profileMeta[pendingOption.value].hoverTitleKey)}</p>
                  <p className="mt-2 text-sm leading-6 text-zinc-300">{t(profileMeta[pendingOption.value].hoverDescriptionKey)}</p>
                  <p className="mt-3 text-xs text-zinc-400">{t('operationalProfile.changeHint')}</p>
                </div>
                <div className="flex flex-col-reverse justify-end gap-2 sm:flex-row">
                  <button type="button" onClick={resetPendingConfirmation} className="rounded-lg border border-white/10 px-4 py-2 text-xs font-semibold text-zinc-300 transition-colors hover:border-white/20 hover:text-white">
                    {t('common.cancel')}
                  </button>
                  <button type="button" disabled={disabled || isConfirming} onClick={() => void confirmProfileChange()} className="rounded-lg bg-brand-700 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-60">
                    {isConfirming ? t('operationalProfile.confirmLoading') : t('operationalProfile.confirmAction')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      ) : null}
    </>
  );
}